import React, { useMemo, useState } from 'react';

import FileDownloadIcon from '@mui/icons-material/FileDownload';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { IconButton, Stack, Tooltip } from '@mui/material';

import GenericDialog from '@host/components/NewUI/Components/Modals/GenericDialog';

import DocumentActions from './components/DocumentActions';
import DocumentPreviewModal from '../components/DocumentPreviewModal';
import Layout from './components/Layout';
import StatusPill from './components/StatusPill';
import {
  CONTRACT_COLS,
  CONTRACT_TABS,
  DOC_COLS,
  DOC_TABS,
  EmptyRow,
  FADE_STYLE,
  formatDate,
  InfoBox,
  PortalRow,
  PortalSection,
  StepLabel,
  TODO_COLS,
  TODO_TABS,
  TodoToggleIcon,
  TYPE_LABEL
} from './portalHomePrimitives';
import { font, palette } from './styles';
import { usePortalCompact } from './usePortalCompact';
import { usePortalDownloads } from './usePortalDownloads';
import { onboardColors } from '../theme/onboardTokens';

import type { TDocTab, TTodoTab } from './portalHomePrimitives';
import type { IPortalItemRow, IPortalState, TPortalView } from './types';

const DATE_CELL_STYLE: React.CSSProperties = { color: palette.black, fontSize: 14 };

interface Props {
  state: IPortalState;
  token: string;
  errorMessage?: string | null;
  onSelectItem: (item: IPortalItemRow) => void;
  onNavigate: (view: TPortalView) => void;
  onCompleteTodo: (item: IPortalItemRow) => Promise<void>;
}

const PortalHome: React.FC<Props> = ({
  state,
  token,
  errorMessage,
  onSelectItem,
  onNavigate,
  onCompleteTodo
}) => {
  const [contractTab, setContractTab] = useState<TDocTab>('need_action');
  const [docTab, setDocTab] = useState<TDocTab>('need_action');
  const [todoTab, setTodoTab] = useState<TTodoTab>('todo');
  const [previewAsset, setPreviewAsset] = useState<{ url: string; name: string } | null>(null);
  const [confirmingTodo, setConfirmingTodo] = useState<IPortalItemRow | null>(null);
  const [completing, setCompleting] = useState(false);
  const compact = usePortalCompact();

  const { downloadContract, downloadLibraryDocument, downloadUploadedAsset, downloadForm } =
    usePortalDownloads(token);

  // Staged reveal: the contract gets its own section, but it appears only once the
  // required pre-offer forms, documents and tasks are complete. Pre-offer items are
  // always visible; post-offer items appear only once the contract is signed.
  // Completed items stay visible as a record.
  const preComplete = state.stages.pre.complete;
  const signComplete = state.stages.sign.complete;
  const contractRow = state.stages.sign.item;

  const docRows = useMemo(() => {
    const rows: IPortalItemRow[] = [...state.stages.pre.items];
    if (signComplete) rows.push(...state.stages.post.items);
    return rows.filter((row) => row.type !== 'todo');
  }, [state, signComplete]);

  const filteredContract = useMemo(() => {
    if (contractTab === 'completed') {
      return contractRow.status === 'submitted' ? [contractRow] : [];
    }
    return contractRow.status !== 'submitted' ? [contractRow] : [];
  }, [contractRow, contractTab]);

  const todoRows = useMemo(() => {
    const rows: IPortalItemRow[] = [...state.stages.pre.items];
    if (signComplete) rows.push(...state.stages.post.items);
    return rows.filter((row) => row.type === 'todo');
  }, [state, signComplete]);

  const filteredDocs = useMemo(() => {
    if (docTab === 'all') return docRows;
    if (docTab === 'completed') return docRows.filter((r) => r.status === 'submitted');
    return docRows.filter((r) => r.status !== 'submitted');
  }, [docRows, docTab]);

  const filteredTodos = useMemo(() => {
    if (todoTab === 'all') return todoRows;
    if (todoTab === 'completed') return todoRows.filter((r) => r.status === 'submitted');
    return todoRows.filter((r) => r.status !== 'submitted');
  }, [todoRows, todoTab]);

  const handleAction = (row: IPortalItemRow) => {
    if (row.status === 'submitted') {
      if (row.type === 'contract_signature') onNavigate('preview');
      else onSelectItem(row);
      return;
    }
    if (row.locked) return;
    if (row.type === 'todo') {
      setConfirmingTodo(row);
      return;
    }
    if (row.type === 'contract_signature') {
      onNavigate('preview');
      return;
    }
    onSelectItem(row);
  };

  const openUploadedAsset = (row: IPortalItemRow) => {
    const latestAssetId = row.asset_ids?.[row.asset_ids.length - 1];
    if (!latestAssetId) return;
    const url = `/onboard/portal/${encodeURIComponent(token)}/assets/${latestAssetId}`;
    setPreviewAsset({ url, name: row.label });
  };

  return (
    <>
      <Layout>
        <style>{FADE_STYLE}</style>
        <h1
          style={{
            fontSize: compact ? 28 : 36,
            fontWeight: 700,
            color: palette.headingNeutral,
            margin: compact ? '16px 0 8px' : '32px 0 8px',
            letterSpacing: '-0.02em',
            fontFamily: font
          }}
        >
          Hi, {state.candidate_name || 'there'}
        </h1>
        <p
          style={{
            color: palette.headingNeutral,
            fontSize: 16,
            margin: '0 0 32px',
            fontFamily: font
          }}
        >
          Congratulations on progressing to the next step!
        </p>
        {errorMessage ? (
          <div
            style={{
              background: onboardColors.statusWash.errorSoft,
              border: `1px solid ${palette.warning}`,
              borderRadius: 12,
              color: palette.warning,
              fontFamily: font,
              fontSize: 14,
              marginBottom: 20,
              padding: '12px 16px'
            }}
          >
            {errorMessage}
          </div>
        ) : null}

        {preComplete ? (
          <PortalSection
            activeTab={contractTab}
            cols={CONTRACT_COLS}
            headers={['Step', 'Status', 'Last submitted']}
            onTabChange={setContractTab}
            tabs={CONTRACT_TABS}
            title="Contract"
            info={
              <InfoBox>
                Please complete the contract in the <strong>Need action</strong> section. Once
                submitted, you can view it anytime in the <strong>Completed</strong> section.
              </InfoBox>
            }
          >
            {filteredContract.length === 0 ? (
              <EmptyRow />
            ) : (
              filteredContract.map((row) => {
                // Submitted contracts are reviewed via the preview/download icons, so
                // only the not-yet-signed (and unlocked) row navigates on click.
                const interactive = row.status !== 'submitted' && row.status !== 'locked';
                const handleRowAction = () => {
                  if (interactive) handleAction(row);
                };
                return (
                  <PortalRow
                    key={row.id}
                    aria-disabled={!interactive}
                    className={`portal-row ${interactive ? 'portal-row--interactive' : ''}`}
                    cols={CONTRACT_COLS}
                    id={`portal-contract-row-${row.id}`}
                    label={<StepLabel row={row} />}
                    onClick={handleRowAction}
                    role="button"
                    style={{ cursor: interactive ? 'pointer' : 'default' }}
                    tabIndex={0}
                    actions={
                      row.status === 'submitted' ? (
                        <div style={{ display: 'inline-flex', gap: 4 }}>
                          <Tooltip title="Preview">
                            <IconButton onClick={() => onNavigate('preview')} size="small">
                              <VisibilityIcon
                                sx={{ color: onboardColors.neutral.icon, fontSize: 20 }}
                              />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Download">
                            <IconButton onClick={downloadContract} size="small">
                              <FileDownloadIcon
                                sx={{ color: onboardColors.neutral.icon, fontSize: 20 }}
                              />
                            </IconButton>
                          </Tooltip>
                        </div>
                      ) : null
                    }
                    cells={[
                      { header: 'Status', content: <StatusPill status={row.status} /> },
                      {
                        header: 'Last submitted',
                        content: formatDate(row.last_submitted_at),
                        style: DATE_CELL_STYLE
                      }
                    ]}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') handleRowAction();
                    }}
                  />
                );
              })
            )}
          </PortalSection>
        ) : null}

        <PortalSection
          activeTab={docTab}
          cols={DOC_COLS}
          headers={['Step', 'Status', 'Last submitted', 'Type']}
          onTabChange={setDocTab}
          tabs={DOC_TABS}
          title="Forms &amp; Documents"
          info={
            <InfoBox>
              Please complete the required forms &amp; documents in the <strong>Need action</strong>{' '}
              section. Once submitted, you can view them anytime in the <strong>Completed</strong>{' '}
              section.
            </InfoBox>
          }
        >
          {filteredDocs.length === 0 ? (
            <EmptyRow />
          ) : (
            filteredDocs.map((row) => (
              <PortalRow
                key={row.id}
                className={`portal-row ${row.status === 'submitted' ? '' : 'portal-row--interactive'}`}
                cols={DOC_COLS}
                id={`portal-document-row-${row.id}`}
                label={<StepLabel row={row} />}
                role={row.locked || row.status === 'submitted' ? undefined : 'button'}
                tabIndex={row.locked || row.status === 'submitted' ? undefined : 0}
                actions={
                  <DocumentActions
                    onDownloadForm={downloadForm}
                    onDownloadLibraryDocument={downloadLibraryDocument}
                    onDownloadUploadedAsset={downloadUploadedAsset}
                    onPreview={handleAction}
                    onPreviewContract={() => onNavigate('preview')}
                    onPreviewUploadedAsset={openUploadedAsset}
                    row={row}
                  />
                }
                cells={[
                  { header: 'Status', content: <StatusPill status={row.status} /> },
                  {
                    header: 'Last submitted',
                    content: formatDate(row.last_submitted_at),
                    style: DATE_CELL_STYLE
                  },
                  {
                    header: 'Type',
                    content: TYPE_LABEL[row.type] ?? row.type,
                    style: { color: palette.bodyNeutral, fontSize: 14 }
                  }
                ]}
                onClick={
                  row.locked || row.status === 'submitted' ? undefined : () => handleAction(row)
                }
                onKeyDown={
                  row.locked || row.status === 'submitted'
                    ? undefined
                    : (e) => {
                        if (e.key === 'Enter' || e.key === ' ') handleAction(row);
                      }
                }
                style={{
                  cursor: row.locked || row.status === 'submitted' ? 'default' : 'pointer'
                }}
              />
            ))
          )}
        </PortalSection>

        {todoRows.length > 0 && (
          <PortalSection
            last
            activeTab={todoTab}
            cols={TODO_COLS}
            headers={['Step', 'Last updated', 'Status']}
            onTabChange={setTodoTab}
            tabs={TODO_TABS}
            title="To-do"
            info={
              <InfoBox>
                Complete the tasks in the <strong>To do</strong> section to finish your onboarding.
              </InfoBox>
            }
          >
            {filteredTodos.length === 0 ? (
              <EmptyRow />
            ) : (
              filteredTodos.map((row) => (
                <PortalRow
                  key={row.id}
                  className="portal-row"
                  cols={TODO_COLS}
                  id={`portal-todo-row-${row.id}`}
                  label={<StepLabel row={row} />}
                  style={{ cursor: 'default' }}
                  actions={
                    <button
                      aria-label="Mark as complete"
                      disabled={row.locked || row.status === 'submitted'}
                      id={`portal-todo-complete-button-${row.id}`}
                      onClick={() => setConfirmingTodo(row)}
                      type="button"
                      style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: row.locked || row.status === 'submitted' ? 'default' : 'pointer',
                        opacity: row.locked ? 0.55 : 1,
                        padding: 0
                      }}
                    >
                      <TodoToggleIcon completed={row.status === 'submitted'} />
                    </button>
                  }
                  cells={[
                    {
                      header: 'Last updated',
                      content: formatDate(row.last_submitted_at),
                      style: DATE_CELL_STYLE
                    },
                    {
                      header: 'Status',
                      content: <StatusPill pendingLabel="To-do" status={row.status} />
                    }
                  ]}
                />
              ))
            )}
          </PortalSection>
        )}
      </Layout>
      {previewAsset ? (
        <DocumentPreviewModal
          assetUrl={previewAsset.url}
          fileName={previewAsset.name}
          onClose={() => setPreviewAsset(null)}
        />
      ) : null}
      <GenericDialog
        alternateColor
        buttonText="Confirm"
        callbackLoading={completing}
        isDialogOpen={confirmingTodo !== null}
        secondaryButtonText="Cancel"
        title="Mark as complete?"
        url=""
        buttonCallback={() => {
          if (!confirmingTodo) return;
          const item = confirmingTodo;
          setCompleting(true);
          onCompleteTodo(item)
            .then(() => setConfirmingTodo(null))
            .finally(() => setCompleting(false));
        }}
        description={
          <Stack>
            <Stack>Once marked as complete, the hiring team will be notified.</Stack>
            <Stack>This action cannot be undone.</Stack>
          </Stack>
        }
        setDialogOpen={(open: boolean) => {
          if (!open) setConfirmingTodo(null);
        }}
      />
    </>
  );
};

export default PortalHome;
